"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MemoryCache = void 0;
const guard_1 = require("../utils/guard");
const utils_1 = require("../resilience/utils");
const cacheError_1 = require("./cacheError");
const memoryQueue_1 = require("./memoryQueue");
const argumentError_1 = require("../utils/argumentError");
const cacheEntry_1 = require("./cacheEntry");
/**
 * A cache that stores its values in memory with a sliding expiration.
 */
class MemoryCache {
    /**
     * Initializes a new instance of the @see MemoryCache class.
     * @param expirationTimespanMs Value in milli seconds how long a value is valid in the cache.
     * @param logger The logger to use.
     * @param garbageCollectEveryXRequests Specifies after how many requests to the cache the garbage collection will take place.
     * @param maxEntryCount Number of maximum entries that can be stored at the same time in the memory cache.
     */
    constructor(expirationTimespanMs, logger, garbageCollectEveryXRequests = 50, maxEntryCount = 1000) {
        /**
         * Dictionary for the cache entries.
         */
        this.entries = {};
        guard_1.Guard.throwIfNullOrEmpty(expirationTimespanMs, 'expirationTimespanMs');
        guard_1.Guard.throwIfNullOrEmpty(logger, 'logger');
        guard_1.Guard.throwIfNullOrEmpty(garbageCollectEveryXRequests, 'garbageCollectEveryXRequests');
        guard_1.Guard.throwIfNullOrEmpty(maxEntryCount, 'maxEntryCount');
        if (garbageCollectEveryXRequests <= 0) {
            throw new argumentError_1.ArgumentError("'garbageCollectEveryXRequests' must be greater than zero.", 'garbageCollectEveryXRequests');
        }
        if (maxEntryCount <= 0) {
            throw new argumentError_1.ArgumentError("'maxEntryCount' must be greater than zero.", 'maxEntryCount');
        }
        this.expirationTimespanMs = expirationTimespanMs;
        this.logger = logger;
        this.garbageCollectEveryXRequests = garbageCollectEveryXRequests;
        this.garbageCounter = 1;
        this.queue = new memoryQueue_1.MemoryQueue(maxEntryCount, logger);
    }
    /**
     * Executes a function within a cache. If the value is in the cache and has not expired, it will be returned from the cache, else it will be queried from the func and added to the cache.
     * @param func Function to get the value if not in cache or has expired.
     * @param key The key to use for this value.
     * @param requestId Request Guid.
     * @returns The result of the provided function.
     */
    execute(func, key, requestId) {
        return __awaiter(this, void 0, void 0, function* () {
            this.garbageCounter++;
            this.garbageCollect();
            if (!key) {
                const error = new cacheError_1.CacheError(`MemoryCache called with 'key' null or empty`);
                this.logger.error(requestId, null, error, utils_1.logFormatter);
                throw error;
            }
            this.logger.trace(requestId, `Starting MemoryCache for key '${key}'`, null, utils_1.logFormatter);
            if (this.isAlreadyInCache(key, requestId) &&
                !this.hasExpired(key, requestId)) {
                const result = this.retrieve(key, requestId);
                return result;
            }
            else {
                try {
                    const result = yield func();
                    const expires = new Date();
                    expires.setMilliseconds(expires.getMilliseconds() + this.expirationTimespanMs);
                    this.insert(key, result, expires, requestId);
                    this.logger.debug(requestId, `Returning value for key '${key}' in MemoryCache.`, null, utils_1.logFormatter);
                    return result;
                }
                catch (e) {
                    const error = new cacheError_1.CacheError(`Error in MemoryCache occured calling func. See 'innerError' for more details.`, e);
                    this.logger.error(requestId, null, error, utils_1.logFormatter);
                    throw error;
                }
            }
        });
    }
    /**
     * Gets the length of items currently stored in the cache.
     */
    length() {
        let i = 0;
        for (const item in this.entries) {
            if (this.entries[item] && this.entries[item].value) {
                i++;
            }
        }
        return i;
    }
    /**
     * Inserts a value into the cache.
     * @param key Key in the cache for new value.
     * @param value Value to insert.
     * @param expires Exipration timestamp for new cache entry.
     * @param requestId Request Guid.
     */
    insert(key, value, expires, requestId) {
        this.entries[key] = new cacheEntry_1.CacheEntry(key, value, expires);
        this.logger.debug(requestId, `Storing value for key '${key}' in MemoryCache with expiration '${expires.toISOString()}'.`, null, utils_1.logFormatter);
        const queueResult = this.queue.push(key, requestId);
        if (queueResult.hasPoped) {
            this.remove(queueResult.popedItem, false, requestId);
        }
    }
    /**
     * Retrieves a value from the cache.
     * @param key Key of value to get.
     * @param requestId Request Guid.
     * @returns The value for a key.
     */
    retrieve(key, requestId) {
        this.logger.debug(requestId, `Returning value for key '${key}' in MemoryCache.`, null, utils_1.logFormatter);
        const result = this.entries[key].value;
        return result;
    }
    /**
     * Gets a value indicating whether a key is already included.
     * @param key Key to check for.
     * @param requestId Request Guid.
     * @returns True if a key is already in the cache, else false.
     */
    isAlreadyInCache(key, requestId) {
        if (this.entries[key] && this.entries[key].value) {
            return true;
        }
        else {
            this.logger.debug(requestId, `Key '${key}' is not in MemoryCache`, null, utils_1.logFormatter);
            return false;
        }
    }
    /**
     * Gets a value indicating whether a key has already expired.
     * @param key Key to check for.
     * @param requestId Request Guid.
     * @returns True if a key has already expired, else false.
     */
    hasExpired(key, requestId) {
        const now = new Date().getTime();
        const value = this.entries[key].expiration;
        const expires = value.getTime();
        if (expires < now) {
            this.logger.debug(requestId, `Key '${key}' in MemoryCache has already expired on '${value.toISOString()}'`, null, utils_1.logFormatter);
            return true;
        }
        else {
            return false;
        }
    }
    /**
     * Removes all items from the cache that have already expired.
     * @returns Number of items removed from the cache.
     */
    garbageCollect() {
        if (this.garbageCounter % this.garbageCollectEveryXRequests === 0) {
            this.logger.trace(null, `Starting garbage collection for MemoryCache.`, null, utils_1.logFormatter);
            const toRemove = [];
            for (const key in this.entries) {
                if (this.hasExpired(key)) {
                    toRemove.push(key);
                }
            }
            for (const key of toRemove) {
                this.remove(key, true);
            }
            this.logger.trace(null, `MemoryCache garbage collection removed '${toRemove.length}' items from the cache.`, null, utils_1.logFormatter);
            this.garbageCounter = 1;
            return toRemove.length;
        }
        return 0;
    }
    /**
     * Clears the memory cache.
     */
    clear() {
        this.logger.warning(null, 'Maintenance: MemoryCache cleared.', null, utils_1.logFormatter);
        for (const prop in this.entries) {
            if (this.entries.hasOwnProperty(prop)) {
                delete this.entries[prop];
            }
        }
        this.garbageCounter = 1;
        this.queue.clear();
    }
    /**
     * Removes an item from the cache.
     * @param key Key of the item to remove from the cache.
     * @param removeFromQueue Flag if value should also be removed from the queue.
     * @param requestId Request Guid.
     */
    remove(key, removeFromQueue, requestId) {
        this.logger.trace(requestId, `Removing key '${key}' from MemoryCache.`, null, utils_1.logFormatter);
        this.entries[key] = null;
        if (removeFromQueue) {
            this.queue.remove(key, requestId);
        }
    }
}
exports.MemoryCache = MemoryCache;
//# sourceMappingURL=memoryCache.js.map